import { ref, computed } from "vue";
import uuid from "@/helpers/uuid";
import { createClipMergeJob } from "@/api/clipMerge";
import { snapTimeToFrame, secondsToTimecode, DEFAULT_VIDEO_FPS } from "@/helpers/videoFps";
import type { VideoClip, ClipSegment, TimelineDragState, ClipMergeJobResponse } from "@/types/clipVideo";

const MIN_CLIP_DURATION = 1;
type ClipCreationResult = {
	created: boolean;
	reason?: "too_short";
};

export function useClipVideo() {
	const clips = ref<VideoClip[]>([]);
	const videoDuration = ref(0);
	const playheadTime = ref(0);
	const isPlaying = ref(false);
	const activeClipId = ref<string | null>(null);
	const isSaving = ref(false);
	const dragState = ref<TimelineDragState | null>(null);
	const previewMode = ref(false);
	const videoFps = ref(DEFAULT_VIDEO_FPS);

	let videoEl: HTMLVideoElement | null = null;
	let previewAnimationId: number | null = null;

	/** Snap a time to the nearest frame boundary using the current video FPS. */
	function snap(time: number): number {
		return snapTimeToFrame(time, videoFps.value);
	}

	const totalClipDuration = computed(() => {
		return clips.value.reduce((sum, clip) => sum + (clip.endTime - clip.startTime), 0);
	});

	const canSave = computed(() => {
		return clips.value.length > 0 && !isSaving.value;
	});

	const clipsAsSegments = computed((): ClipSegment[] => {
		const fps = videoFps.value;
		return clips.value.map((clip) => ({
			startTimecode: secondsToTimecode(clip.startTime, fps),
			endTimecode: secondsToTimecode(clip.endTime, fps)
		}));
	});

	function setVideoElement(el: HTMLVideoElement) {
		videoEl = el;
	}

	function setVideoDuration(duration: number) {
		videoDuration.value = duration;
	}

	function setVideoFps(fps: number) {
		videoFps.value = fps > 0 ? fps : DEFAULT_VIDEO_FPS;
	}

	/** Check whether a time range overlaps any existing clip. */
	function overlapsExistingClip(start: number, end: number, excludeId?: string): boolean {
		return clips.value.some(
			(c) => (excludeId ? c.id !== excludeId : true) && start < c.endTime && end > c.startTime
		);
	}

	/**
	 * Return the bounds of the "gap" that contains `anchorTime`.
	 * Clips must be sorted by startTime for correct results (they always are).
	 */
	function getGapBounds(anchorTime: number): { min: number; max: number } {
		let min = 0;
		let max = videoDuration.value;
		for (const clip of clips.value) {
			if (clip.endTime <= anchorTime) {
				min = clip.endTime;
			} else if (clip.startTime >= anchorTime) {
				max = clip.startTime;
				break;
			}
		}
		return { min, max };
	}

	function addClip(startTime: number, endTime: number): ClipCreationResult {
		// Snap to frame boundaries then clamp to video bounds
		const clampedStart = Math.max(0, snap(startTime));
		const clampedEnd = Math.min(videoDuration.value, snap(endTime));

		if (clampedEnd - clampedStart < MIN_CLIP_DURATION) {
			return { created: false, reason: "too_short" };
		}

		// Reject if the new clip overlaps any existing clip
		if (overlapsExistingClip(clampedStart, clampedEnd)) {
			return { created: false };
		}

		const clip: VideoClip = {
			id: uuid.uuidv4(),
			startTime: clampedStart,
			endTime: clampedEnd
		};
		clips.value.push(clip);

		// Keep clips ordered by their position on the timeline
		clips.value.sort((a, b) => a.startTime - b.startTime);

		activeClipId.value = clip.id;
		return { created: true };
	}

	function removeClip(id: string) {
		const index = clips.value.findIndex((c) => c.id === id);
		if (index !== -1) {
			clips.value.splice(index, 1);
			if (activeClipId.value === id) {
				activeClipId.value = null;
			}
		}
	}

	function seekTo(time: number) {
		const clamped = Math.max(0, Math.min(videoDuration.value, snap(time)));
		playheadTime.value = clamped;
		if (videoEl) {
			videoEl.currentTime = clamped;
		}
	}

	function togglePlayPause() {
		if (!videoEl) return;
		if (isPlaying.value) {
			videoEl.pause();
			// Snap to nearest frame boundary on pause so the playhead
			// rests on an exact frame, not an arbitrary mid-frame position.
			const snapped = Math.max(0, Math.min(videoDuration.value, snap(videoEl.currentTime)));
			videoEl.currentTime = snapped;
			playheadTime.value = snapped;
		} else {
			// Snap before starting playback so it begins on a frame boundary.
			const snapped = Math.max(0, Math.min(videoDuration.value, snap(videoEl.currentTime)));
			videoEl.currentTime = snapped;
			playheadTime.value = snapped;
			videoEl.play();
		}
	}

	function startDrag(startTime: number) {
		// Don't start a drag inside an existing clip region
		const insideClip = clips.value.some(
			(c) => startTime >= c.startTime && startTime <= c.endTime
		);
		if (insideClip) return;

		// Snap to nearest frame boundary
		const snappedTime = snap(startTime);

		// Pause playback so the frame stays still while dragging
		if (videoEl && !videoEl.paused) {
			videoEl.pause();
		}
		dragState.value = {
			isDragging: true,
			startTime: snappedTime,
			currentTime: snappedTime
		};
		// Show the frame at the drag start point
		if (videoEl) {
			videoEl.currentTime = Math.max(0, Math.min(videoDuration.value, snappedTime));
		}
	}

	function updateDrag(currentTime: number) {
		if (!dragState.value) return;
		// Clamp the drag endpoint so the resulting region stays within the
		// gap that contains the drag anchor (prevents overlapping existing clips).
		const bounds = getGapBounds(dragState.value.startTime);
		const clampedTime = Math.max(bounds.min, Math.min(bounds.max, currentTime));
		// Snap to nearest frame boundary after clamping
		const snappedTime = snap(clampedTime);
		dragState.value = {
			...dragState.value,
			currentTime: snappedTime
		};
		// Live preview: seek video to the drag endpoint so the user sees the frame
		if (videoEl) {
			videoEl.currentTime = Math.max(0, Math.min(videoDuration.value, snappedTime));
		}
	}

	function commitDrag(): ClipCreationResult {
		if (!dragState.value) return { created: false };
		const start = Math.min(dragState.value.startTime, dragState.value.currentTime);
		const end = Math.max(dragState.value.startTime, dragState.value.currentTime);
		dragState.value = null;
		return addClip(start, end);
	}

	function cancelDrag() {
		dragState.value = null;
	}

	function previewAllClips() {
		if (!videoEl || clips.value.length === 0) return;
		previewMode.value = true;
		let clipIndex = 0;

		function playNextClip() {
			if (!previewMode.value || clipIndex >= clips.value.length || !videoEl) {
				stopPreview();
				return;
			}
			const clip = clips.value[clipIndex];

			// Immediately update reactive state so the playhead and active clip
			// are in sync even before the video element finishes seeking.
			activeClipId.value = clip.id;
			playheadTime.value = clip.startTime;

			// Wait for the video to actually seek before starting playback.
			// On mobile browsers, setting currentTime is asynchronous; playing
			// before the seek completes causes the playhead to show a stale position.
			const el = videoEl;
			el.currentTime = clip.startTime;

			function onSeeked() {
				el.removeEventListener("seeked", onSeeked);
				if (!previewMode.value) {
					stopPreview();
					return;
				}
				el.play();
				previewAnimationId = requestAnimationFrame(checkTime);
			}

			function checkTime() {
				if (!previewMode.value || !videoEl) {
					stopPreview();
					return;
				}
				// Keep playheadTime in sync from within the preview loop itself,
				// so it stays accurate even if the external rAF sync loop lags.
				playheadTime.value = videoEl.currentTime;

				if (videoEl.currentTime >= clip.endTime) {
					videoEl.pause();
					// The rAF poll fires a few ms after the real crossing, so
					// currentTime has overshot clip.endTime. Seek back to the
					// exact (frame-snapped) end so the playhead never rests
					// past the clip boundary.
					videoEl.currentTime = clip.endTime;
					playheadTime.value = clip.endTime;
					clipIndex++;
					playNextClip();
					return;
				}
				previewAnimationId = requestAnimationFrame(checkTime);
			}

			el.addEventListener("seeked", onSeeked, { once: true });
		}

		playNextClip();
	}

	function stopPreview() {
		previewMode.value = false;
		if (previewAnimationId !== null) {
			cancelAnimationFrame(previewAnimationId);
			previewAnimationId = null;
		}
		if (videoEl) {
			videoEl.pause();
			// Snap to nearest frame boundary so the playhead rests on an exact frame.
			const snapped = Math.max(0, Math.min(videoDuration.value, snap(videoEl.currentTime)));
			videoEl.currentTime = snapped;
			playheadTime.value = snapped;
		}
	}

	async function save(
		recordingId: string,
		conversationId?: string,
		projectId?: string
	): Promise<ClipMergeJobResponse> {
		isSaving.value = true;
		try {
			const response = await createClipMergeJob({
				masterRecordingSourceId: recordingId,
				segments: clipsAsSegments.value,
				conversationId,
				projectId
			});
			return response;
		} finally {
			isSaving.value = false;
		}
	}

	function reset() {
		stopPreview();
		clips.value = [];
		videoDuration.value = 0;
		playheadTime.value = 0;
		isPlaying.value = false;
		activeClipId.value = null;
		isSaving.value = false;
		dragState.value = null;
		videoFps.value = DEFAULT_VIDEO_FPS;
		videoEl = null;
	}

	return {
		clips,
		videoDuration,
		playheadTime,
		isPlaying,
		activeClipId,
		isSaving,
		dragState,
		previewMode,
		MIN_CLIP_DURATION,
		totalClipDuration,
		canSave,
		clipsAsSegments,
		setVideoElement,
		setVideoDuration,
		setVideoFps,
		addClip,
		removeClip,
		seekTo,
		togglePlayPause,
		startDrag,
		updateDrag,
		commitDrag,
		cancelDrag,
		previewAllClips,
		stopPreview,
		save,
		reset
	};
}
